import {
  MAX_CHARACTER_EMAIL_VALIDATION,
  MAX_CHARACTER_FULLNAME_VALIDATION,
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_CHARACTER_PHONE_VALIDATION,
  MAX_CHARACTER_ZIP_CODE,
  MAX_COMPANY_ADDRESS_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
} from "@/constants/common";
import { INVALID_NUMBER_MAX, INVALID_NUMBER_MIN, INVALID_DATE_MUST_BEFORE, INVALID_DATE_MUST_AFTER } from "@/constants/invalids";
import { ERecruitmentStatus } from "@/types/enum";
import { replaceStr } from "@/utils/string";
import { validator } from "@/utils/validator";
import * as yup from "yup";
import dayjs from "dayjs";
import { useMemo } from "react";
import { calculateSpecificSkillTransitionPeriod } from "@/features/talent/utils/calculateSpecificSkillTransitionPeriod";

const isValueEmpty = (val: unknown): boolean => {
  if (val === null || val === undefined) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  return false;
};

type EmptyRowFieldConfig = { name: string; label: string; isSelection: boolean };

const createEmptyRowTest = (fieldConfigs: EmptyRowFieldConfig[]) => {
  const fieldNames = fieldConfigs.map((f) => f.name);
  return {
    name: "no-empty-rows",
    test: function (this: yup.TestContext, value: unknown[] | null | undefined) {
      const isFormSubmitted = (this.options.context as Record<string, unknown>)?.isFormSubmitted ?? true;
      if (!isFormSubmitted) return true;

      if (!value || value.length < 2) return true;

      const errors: yup.ValidationError[] = [];
      value.forEach((item, index) => {
        const row = item as Record<string, unknown>;
        if (!row || fieldNames.every((fn) => isValueEmpty(row[fn]))) {
          fieldConfigs.forEach(({ name, label, isSelection }) => {
            errors.push(
              new yup.ValidationError(
                isSelection ? `「${label}」を選択してください。` : `「${label}」を入力してください。`,
                null,
                `${this.path}[${index}].${name}`,
              ),
            );
          });
        }
      });
      if (errors.length > 0) throw new yup.ValidationError(errors);
      return true;
    },
  };
};

const EDUCATION_FIELD_CONFIGS: EmptyRowFieldConfig[] = [
  { name: "educationLevelId", label: "学歴", isSelection: true },
  { name: "schoolName", label: "学校名", isSelection: false },
  { name: "countryId", label: "国名", isSelection: true },
  { name: "enrollmentDate", label: "入学年月", isSelection: true },
  { name: "graduationDate", label: "卒業年月", isSelection: true },
];

const WORK_EXPERIENCE_FIELD_CONFIGS: EmptyRowFieldConfig[] = [
  { name: "jobType", label: "職種", isSelection: false },
  { name: "companyName", label: "企業名", isSelection: false },
  { name: "countryId", label: "国名", isSelection: true },
  { name: "startDate", label: "入社年月", isSelection: true },
  { name: "endDate", label: "退職年月", isSelection: true },
];

const LANGUAGE_SKILLS_FIELD_CONFIGS: EmptyRowFieldConfig[] = [
  { name: "language", label: "語学（日本語以外の言語）", isSelection: true },
  { name: "content", label: "語学（日本語以外の言語）の内容", isSelection: false },
];

/**
 * Fields rendered exclusively inside `TalentFormResidenceStatus` (在留資格情報 block).
 * The resume-only scan tab does not render this block, so its schema must skip
 * validating these fields — otherwise hidden errors block submission with no
 * visible feedback (see IROHANA_MATCH_DEV-280: 特定技能1号残存期間 silent error
 * on the 履歴書項目 tab).
 */
const RESIDENCE_FIELD_KEYS = [
  "residenceStatusId",
  "periodOfStay",
  "isBeforeEntry",
  "specifiedSkillFirstStartDate",
  "blankPeriodMonthly",
  "specifiedSkillFirstRemainingTerm",
  "passportNumber",
  "passportExpiryDate",
  "cardNumber",
] as const;

type UseGetTalentSchemaOptions = {
  /** When true, residence-status fields are accepted as-is (no validation). */
  omitResidenceFields?: boolean;
};

export const useGetTalentSchema = (options: UseGetTalentSchemaOptions = {}) => {
  const { omitResidenceFields = false } = options;
  const talentSchema = useMemo(() => {
    const baseShape = {
      recruitmentStatus: validator.number().isSelection().emptyToNull().label("求職活動ステータス").required(),
      matchOfficeId: validator
        .number()
        .isSelection()
        .emptyToNull()
        .nullable()
        .label("紹介事業所名")
        .optional()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      // Draft save: fullName required only when nickName is also empty
      // Non-draft: fullName always required with standard message
      fullName: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("氏名")
        .nameOnly()
        .latinsOnly()
        .autoTransformToUpperCase()
        .max(MAX_CHARACTER_FULLNAME_VALIDATION)
        .test("fullName-conditional-required", "", function (value) {
          const { recruitmentStatus, nickName } = this.parent;
          const isDraft = recruitmentStatus === ERecruitmentStatus.DRAFT;

          if (isDraft && nickName?.trim()) return true;
          if (value?.trim()) return true;

          return this.createError({
            message: isDraft ? "「氏名」または「呼び名」を入力してください。" : "「氏名」を入力してください。",
          });
        }),
      fullNameKana: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .hiraganaToKatakana()
        .katakanaOnly()
        .autoTransformToFullWidth()
        .label("カナ表記")
        .notRequired()
        .max(MAX_CHARACTER_FULLNAME_VALIDATION),
      isRecommendedTalent: validator.boolean().notRequired().optional().label(""),
      birthday: validator.mixed().isDayjs().isSelection().isBeforeAge(18).nullable().label("生年月日").notRequired(),
      nickName: validator
        .string()
        .trim()
        .emptyToNull()
        .hiraganaToKatakana()
        .katakanaOnly()
        .autoTransformToFullWidth()
        .label("呼び名")
        .notRequired()
        .max(MAX_CHARACTER_FULLNAME_VALIDATION),
      talentNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .notComma()
        .removeSpaces()
        .label("人材管理番号")
        .notRequired()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      nationalityId: validator
        .number()
        .isSelection()
        .emptyToNull()
        .label("国籍")
        .optional()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      gender: validator
        .number()
        .isSelection()
        .emptyToNull()
        .label("性別")
        .optional()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      phoneNumber: validator
        .string()
        .emptyToNull()
        .isTelNumber()
        .label("電話番号")
        .autoTransformToHalfWidth()
        .notRequired()
        .max(MAX_CHARACTER_PHONE_VALIDATION),
      email: validator.string().email().emptyToNull().label("メールアドレス").notRequired().max(MAX_CHARACTER_EMAIL_VALIDATION),
      religion: validator.string().trim().emptyToNull().label("宗教").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      location: validator
        .number()
        .isSelection()
        .optional()
        .emptyToNull()
        .label("居住地")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      zipCode: validator
        .string()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .zipCode()
        .label("郵便番号")
        .optional()
        .max(MAX_CHARACTER_ZIP_CODE)
        .notRequired(),
      prefecture: validator.string().trim().emptyToNull().label("所在地（都道府県）").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      city: validator.string().trim().emptyToNull().label("所在地（市区町村）").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      address: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("所在地（町名丁目番地号等）")
        .autoTransformToFullWidth()
        .notRequired()
        .max(MAX_COMPANY_ADDRESS_INPUT_VALIDATION),
      country: validator.string().trim().emptyToNull().nullable().label("国・地域").notRequired().max(MAX_COMPANY_ADDRESS_INPUT_VALIDATION),
      periodOfStay: validator.mixed().isDayjs().isSelection().nullable().label("在留期限").notRequired(),
      isBeforeEntry: validator.boolean().label("未入国・初回申請前").notRequired().nullable(),
      specifiedSkillFirstStartDate: validator.mixed().isDayjs().isSelection().isPastOrToday().label("特定技能1号起算日").notRequired().nullable(),
      blankPeriodMonthly: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .numbersOnly()
        .min(0, replaceStr(INVALID_NUMBER_MIN, { path: "ブランク期間（月単位）", min: 0 }))
        .maxValue(600, replaceStr(INVALID_NUMBER_MAX, { path: "ブランク期間（月単位）", max: 600 }))
        .label("ブランク期間（月単位）")
        .notRequired(),
      specifiedSkillFirstRemainingTerm: validator
        .string()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .label("特定技能1号残存期間")
        .nullable()
        .notRequired()
        .when(
          ["specifiedSkillFirstStartDate", "blankPeriodMonthly", "isBeforeEntry"],
          ([specifiedSkillFirstStartDate, blankPeriodMonthly, isBeforeEntry], schema) => {
            return schema.test("checkSpecifiedSkillFirstRemainingTerm", "", function (value) {
              const { path, createError } = this;

              // Skip validation when "未入国・初回申請前" is checked — remaining is hardcoded 5yr
              if (isBeforeEntry) return true;

              if (!value || !specifiedSkillFirstStartDate) return true;

              const FIVE_YEARS_IN_MONTHS = 5 * 12;
              const { remainingMonths } = calculateSpecificSkillTransitionPeriod({
                specifiedSkillFirstStartDate,
                blankPeriodMonthly: blankPeriodMonthly?.toString(),
              });

              if (remainingMonths < 0) {
                return createError({
                  path,
                  message: "「特定技能1号残存期間」は0年0か月以上を設定してください。",
                });
              }

              if (remainingMonths > FIVE_YEARS_IN_MONTHS) {
                return createError({
                  path,
                  message: "「特定技能1号残存期間」は最大5年0か月までに設定してください。",
                });
              }

              return true;
            });
          },
        ),
      passportNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .autoTransformToUpperCase()
        .removeSpaces()
        .uppercaseAlphanumericCharactersOnly()
        .max(9)
        .label("パスポート番号")
        .notRequired()
        .test((value, ctx) => {
          if (ctx.parent.passportExpired && !ctx.parent.passportNumber) return ctx.createError({ message: "「パスポート番号」を入力してください。" });
          return true;
        }),
      passportExpiryDate: validator
        .mixed()
        .isSelection()
        .isDayjs()
        .label("パスポート期限")
        .notRequired()
        .test((value, ctx) => {
          if (!ctx.parent.passportExpiryDate && ctx.parent.passportCode) return ctx.createError({ message: "「旅券有効期限」を選択してください。" });
          return true;
        }),
      cardNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .autoTransformToUpperCase()
        .removeSpaces()
        .uppercaseAlphanumericCharactersOnly()
        .max(12)
        .label("在留カード番号")
        .nullable()
        .notRequired(),
      residenceStatusId: validator.number().isSelection().label("在留資格").notRequired().nullable(),
      educationBackground: validator
        .array()
        .of(
          validator.object().shape({
            educationLevelId: validator.number().isSelection().label("学歴").emptyToNull().nullable().notRequired(),
            schoolName: validator.string().trim().emptyToNull().label("学校名").nullable().max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
            countryId: validator.number().isSelection().label("国名").emptyToNull().nullable().notRequired(),
            enrollmentDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .label("入学年月")
              .nullable()
              .notRequired()
              .test("is-before-graduation", replaceStr(INVALID_DATE_MUST_BEFORE, { start: "入学年月", end: "卒業年月" }), function (value) {
                const { graduationDate } = this.parent;
                if (!value || !graduationDate) return true;
                return dayjs(value).isBefore(graduationDate) || dayjs(value).isSame(graduationDate);
              }),
            graduationDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .label("卒業年月")
              .nullable()
              .notRequired()
              .test("is-after-enrollment", replaceStr(INVALID_DATE_MUST_AFTER, { start: "入学年月", end: "卒業年月" }), function (value) {
                const { enrollmentDate } = this.parent;
                if (!value || !enrollmentDate) return true;
                return dayjs(value).isAfter(enrollmentDate) || dayjs(value).isSame(enrollmentDate);
              }),
          }),
        )
        .test(createEmptyRowTest(EDUCATION_FIELD_CONFIGS))
        .notRequired()
        .nullable(),
      heldQualifications: validator
        .array()
        .of(validator.string().label("資格").nullable().emptyToNull().max(MAX_CHARACTER_INPUT_VALIDATION))
        .test({
          name: "no-empty-items",
          test: function (value) {
            if (!value || value.length < 2) return true;
            const errors: yup.ValidationError[] = [];
            value.forEach((item, index) => {
              if (isValueEmpty(item)) {
                errors.push(new yup.ValidationError("「資格」を入力してください。", null, `${this.path}[${index}]`));
              }
            });
            if (errors.length > 0) throw new yup.ValidationError(errors);
            return true;
          },
        })
        .notRequired(),
      languageTestLevelIds: validator
        .array()
        .label("日本語能力証明書等（実習修了証明含む）")
        .of(validator.number().isSelection().emptyToNull().nullable())
        .notRequired()
        .nullable(),
      languageTestOther: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("日本語能力（その他）")
        .notRequired()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      specificSkillCertificates: validator.array().of(validator.number().emptyToNull().nullable()).label("特定技能合格証等").notRequired().nullable(),
      companySpecificIndustryId: validator
        .number()
        .isSelection()
        .label("分野")
        .emptyToNull()
        .nullable()
        .notRequired()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      specificBusinessClassificationIds: validator
        .array()
        .of(validator.number().isSelection().emptyToNull().nullable())
        .label("区分")
        .nullable()
        .notRequired(),
      majorId: validator.number().label("技能実習　職種").notRequired().nullable(),
      jobTypeId: validator.number().label("作業").notRequired().nullable(),
      languageSkills: validator
        .array()
        .of(
          validator.object().shape({
            language: validator.number().isSelection().label("語学（日本語以外の言語）").nullable().emptyToNull().notRequired(),
            content: validator
              .string()
              .trim()
              .emptyToNull()
              .label("語学（日本語以外の言語）の内容")
              .nullable()
              .max(MAX_CHARACTER_INPUT_VALIDATION)
              .notRequired(),
          }),
        )
        .test(createEmptyRowTest(LANGUAGE_SKILLS_FIELD_CONFIGS))
        .notRequired()
        .nullable(),
      workExperiences: validator
        .array()
        .of(
          validator.object().shape({
            jobType: validator.string().nullable().label("職種").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
            companyName: validator.string().nullable().label("企業名").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
            startDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .nullable()
              .label("入社年月")
              .notRequired()
              .test("is-before-end", replaceStr(INVALID_DATE_MUST_BEFORE, { start: "入社年月", end: "退職年月" }), function (value) {
                const { endDate } = this.parent;
                if (!value || !endDate) return true;
                return dayjs(value).isBefore(endDate) || dayjs(value).isSame(endDate);
              }),
            endDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .nullable()
              .label("退職年月")
              .notRequired()
              .test("is-after-start", "「退職年月」は「入社年月」より後にする必要があります。", function (value) {
                const { startDate } = this.parent;
                if (!value || !startDate) return true;
                return dayjs(value).isAfter(startDate) || dayjs(value).isSame(startDate);
              }),
          }),
        )
        .test(createEmptyRowTest(WORK_EXPERIENCE_FIELD_CONFIGS))
        .optional()
        .nullable(),
      memo: validator.string().emptyToNull().label("特性メモ").nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      interviewEvaluation: validator.string().label("面談評価").emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      familyInformation: validator.string().label("家族構成").emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      desiredJobTypeIds: validator
        .array()
        .of(validator.number().isSelection().emptyToNull().nullable())
        .notRequired()
        .nullable()
        .label("希望職種（分野）"),
      contactMethods: validator
        .array()
        .of(
          validator.object().shape({
            contactType: validator.number().isSelection().emptyToNull().nullable().notRequired().label("その他の連絡手段、SNS"),
            contactInfo: validator.string().emptyToNull().nullable().notRequired().label("その他の連絡先ID等").max(MAX_CHARACTER_INPUT_VALIDATION),
          }),
        )
        .notRequired()
        .nullable(),
      desiredWorkLocation: validator.string().emptyToNull().nullable().notRequired().label("希望勤務地").max(MAX_CHARACTER_INPUT_VALIDATION),
      expectSalary: validator.string().emptyToNull().nullable().notRequired().max(MAX_CHARACTER_INPUT_VALIDATION).label("希望給与"),
      availableFrom: validator.string().emptyToNull().label("転職可能時期").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      otherRequest: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired().label("その他希望"),
      internalMemo: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired().label("社内メモ"),
      interviewDate: validator.mixed().isSelection().isDayjs().label("面談日").notRequired().nullable(),
      interviewerId: validator.number().emptyToNull().nullable().notRequired().label("面談担当者"),
      partnerId: validator.number().label("パートナー名").notRequired().nullable(),
      personInChargeId: validator.number().isSelection().label("人材担当者").required(),
      relatedFileTypes: validator.array().of(validator.string().nullable().isSelection().required().label("人材添付書類の種類")).nullable(),
      partnerManagementNumber: validator.string().emptyToNull().nullable().notRequired().label("パートナー管理番号"),
      partnerNationality: validator.number().isSelection().emptyToNull().nullable().notRequired().label("国籍"),
      partnerZipCode: validator.string().emptyToNull().nullable().notRequired().label("郵便番号"),
      partnerPrefecture: validator.string().emptyToNull().nullable().notRequired().label("都道府県"),
      partnerCity: validator.string().emptyToNull().nullable().notRequired().label("市区町村"),
      partnerAddress: validator.string().emptyToNull().nullable().notRequired().label("都道府県・市区町村以外の住所"),
      partnerLocation: validator.string().emptyToNull().nullable().notRequired().label("所在地"),
      partnerContactPerson: validator.string().emptyToNull().nullable().notRequired().label("担当者"),
      partnerContactMethod: validator.number().isSelection().emptyToNull().nullable().notRequired().label("連絡方法"),
      partnerPhoneNumber: validator.string().emptyToNull().nullable().notRequired().label("連絡先"),
      partnerEmail: validator.string().emptyToNull().nullable().notRequired().label("メールアドレス"),
      partnerOtherContacts: validator.string().emptyToNull().nullable().notRequired().label("その他の連絡先"),
      partnerLaborBureauStatus: validator.boolean().notRequired().label("労働局届出状況"),
      partnerContractInfo: validator.string().emptyToNull().nullable().notRequired().label("契約情報"),
      partnerBillingInfo: validator.string().emptyToNull().nullable().notRequired().label("請求情報"),
      partnerMemo: validator.string().emptyToNull().nullable().notRequired().label("メモ"),
    };

    if (omitResidenceFields) {
      // Replace each residence field's validator with a permissive no-op so
      // values stay in the payload but validation never fires on the resume tab.
      const permissive = validator.mixed().nullable().notRequired();
      const overrides = RESIDENCE_FIELD_KEYS.reduce<Record<string, typeof permissive>>((acc, key) => {
        acc[key] = permissive;
        return acc;
      }, {});
      return validator.object().shape({ ...baseShape, ...overrides });
    }

    return validator.object().shape(baseShape);
  }, [omitResidenceFields]);

  return { talentSchema };
};
